import { useTranslation } from "react-i18next";
import {
  Share,
  FacebookLogo,
  LinkedinLogo,
  XLogo,
  WhatsappLogo,
  LinkSimple,
  type Icon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "@/lib/toast";

interface ShareButtonProps {
  url: string;
  title: string;
}

function openShareWindow(targetUrl: string): void {
  window.open(targetUrl, "_blank", "noopener,noreferrer");
}

export function ShareButton({ url, title }: ShareButtonProps) {
  const { t } = useTranslation();
  const encUrl = encodeURIComponent(url);
  const encTitle = encodeURIComponent(title);

  const targets: Array<{ key: string; label: string; icon: Icon; onClick: () => void }> = [
    {
      key: "facebook",
      label: t("blogPost.shareFacebook"),
      icon: FacebookLogo,
      onClick: () => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encUrl}`),
    },
    {
      key: "linkedin",
      label: t("blogPost.shareLinkedin"),
      icon: LinkedinLogo,
      onClick: () =>
        openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`),
    },
    {
      key: "twitter",
      label: t("blogPost.shareTwitter"),
      icon: XLogo,
      onClick: () =>
        openShareWindow(`https://twitter.com/intent/tweet?url=${encUrl}&text=${encTitle}`),
    },
    {
      key: "whatsapp",
      label: t("blogPost.shareWhatsapp"),
      icon: WhatsappLogo,
      onClick: () => openShareWindow(`https://wa.me/?text=${encTitle}%20${encUrl}`),
    },
    {
      key: "copy",
      label: t("blogPost.shareCopy"),
      icon: LinkSimple,
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(url);
          toast.success(t("blogPost.shareCopied"));
        } catch {
          toast.error(t("blogPost.shareCopy"));
        }
      },
    },
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Share className="size-4" weight="bold" />
          {t("blogPost.share")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <ul className="flex flex-col">
          {targets.map((target) => {
            const TargetIcon = target.icon;
            return (
              <li key={target.key}>
                <button
                  type="button"
                  onClick={target.onClick}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
                >
                  <TargetIcon className="size-4 text-muted-foreground" weight="duotone" />
                  <span className="text-foreground">{target.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
